import React from "react";
import { Stack, Box, Typography, InputAdornment } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendIcon from "@mui/icons-material/Send";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { forgotPassword } from "../redux/actions/authActions";
import { cleanFormData } from "../helpers/formHelpers";
import { authActions } from "../redux/slices/auth";
import { EmailCompanyCodeRequestDto } from "../shared/dtos/auth/EmailCompanyCodeRequestDto";

const forgotPasswordSchema = Yup.object().shape({
  companyCode: Yup.string().required("Company Code is required"),
  email: Yup.string()
    .email("Invalid email")
    .required("Email Address is required"),
});

interface ForgotPasswordFormProps {
  loading?: boolean;
  error?: string | null;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { company } = useAppSelector((state) => state.company);

  const initialValues = {
    companyCode: company?.companyCode || "",
    email: "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(authActions.setErrorForgotPassword(null))}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={forgotPasswordSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(values);
          dispatch(forgotPassword(payload));
        }}
      >
        {({ dirty, isValid }: FormikProps<EmailCompanyCodeRequestDto>) => (
          <Form>
            <Stack spacing={3}>
              <Typography
                sx={{ color: "text.secondary", fontSize: "0.875rem", mb: 1 }}
              >
                Enter your company code and the email address associated with
                your account.
              </Typography>

              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr" }}>
                <FormTextField
                  name="companyCode"
                  label="Company Code"
                  placeholder="e.g., 01"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <FormTextField
                  name="email"
                  label="Email Address"
                  type="email"
                  placeholder="name@company.com"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>

              <SubmitButton
                title="Send Reset Link"
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={<SendIcon />}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ForgotPasswordForm;
